import {
  RecipeDefinition,
  SlotRecipeDefinition,
  SystemConfig,
  defineConfig,
} from "@chakra-ui/react/styled-system";
import AccordionStyles from "./components/Accordion";
import ActionBarStyles from "./components/ActionBar";
import ButtonStyles from "./components/Button";
import CardStyles from "./components/Card";
import ComboboxStyles from "./components/Combobox";
import DatePickerStyles from "./components/DatePicker";
import HeadingStyles from "./components/Heading";
import InputStyles from "./components/Input";
import LinkStyles from "./components/Link";
import ListStyles from "./components/List";
import MenuStyles from "./components/Menu";
import ModalStyles from "./components/Modal";
import NumberInputStyles from "./components/NumberInput";
import PopoverStyles from "./components/Popover";
import RadioCardStyles from "./components/RadioCard";
import SegmentGroupStyles from "./components/SegmentGroup";
import SelectStyles from "./components/Select";
import SliderStyles from "./components/Slider";
import SwitchStyles from "./components/Switch";
import TableStyles from "./components/Table";
import TabsStyles from "./components/Tabs";
import TagStyles from "./components/Tag";
import TagsInputStyles from "./components/TagsInput";
import TextStyles from "./components/Text";
import TextareaStyles from "./components/Textarea";
import ToolTipStyles from "./components/ToolTip";
import TreeViewStyles from "./components/TreeView";

const recipes: Record<string, RecipeDefinition> = {
  link: LinkStyles,
  input: InputStyles,
  button: ButtonStyles,
  heading: HeadingStyles,
  text: TextStyles,
  textarea: TextareaStyles,
};

const slotRecipes: Record<string, SlotRecipeDefinition> = {
  tag: TagStyles,
  card: CardStyles,
  dialog: ModalStyles,
  popover: PopoverStyles,
  numberInput: NumberInputStyles,
  menu: MenuStyles,
  list: ListStyles,
  tabs: TabsStyles,
  accordion: AccordionStyles,
  actionBar: ActionBarStyles,
  switch: SwitchStyles,
  slider: SliderStyles,
  table: TableStyles,
  tooltip: ToolTipStyles,
  select: SelectStyles,
  combobox: ComboboxStyles,
  datePicker: DatePickerStyles,
  treeView: TreeViewStyles,
  segmentGroup: SegmentGroupStyles,
  radioCard: RadioCardStyles,
  tagsInput: TagsInputStyles,
};

// Used this to generate palettes https://palette.saas-ui.dev/
const FuzeyTheme: SystemConfig = defineConfig({
  theme: {
    recipes,
    slotRecipes,
    tokens: {
      colors: {
        white: {
          value: "#F2F2F2",
        },
        black: {
          value: "#101010",
        },
        blue: {
          50: { value: "#D9E8FF" },
          100: { value: "#C2DAFF" },
          200: { value: "#A7CBFF" },
          300: { value: "#85B4FF" },
          400: { value: "#5893FF" },
          500: { value: "#007df2" },
          600: { value: "#0054a2" },
          700: { value: "#00376a" },
          800: { value: "#00213f" },
          900: { value: "#0a1546" },
          950: { value: "#080F32" },
        },
        gray: {
          50: { value: "#DADCE5" },
          100: { value: "#C5C7D0" },
          200: { value: "#ACADB8" },
          300: { value: "#9393A0" },
          400: { value: "#797B89" },
          500: { value: "#616272" },
          600: { value: "#4B4C58" },
          700: { value: "#363741" },
          800: { value: "#21222A" },
          900: { value: "#16171C" },
          950: { value: "#0F1013" },
        },
        green: {
          50: { value: "#DFF4EB" },
          100: { value: "#BCE8D6" },
          200: { value: "#98DBC1" },
          300: { value: "#75CEAC" },
          400: { value: "#52C097" },
          500: { value: "#3CA87F" },
          600: { value: "#2E8664" },
          700: { value: "#21644B" },
          800: { value: "#154332" },
          900: { value: "#0B261D" },
          950: { value: "#061510" },
        },
        purple: {
          50: { value: "#EFE8FF" },
          100: { value: "#D5C7FD" },
          200: { value: "#BBA7FC" },
          300: { value: "#A287FA" },
          400: { value: "#895FF8" },
          500: { value: "#7139F6" },
          600: { value: "#5B2DC5" },
          700: { value: "#452196" },
          800: { value: "#30156A" },
          900: { value: "#1F0E47" },
          950: { value: "#13092D" },
        },
        pink: {
          50: { value: "#FBE8F0" },
          100: { value: "#F5C5DC" },
          200: { value: "#EF9FC6" },
          300: { value: "#E978AE" },
          400: { value: "#E35197" },
          500: { value: "#D02676" },
          600: { value: "#A81E5E" },
          700: { value: "#82174A" },
          800: { value: "#5C1036" },
          900: { value: "#3D0B24" },
          950: { value: "#240617" },
        },
        orange: {
          50: { value: "#FFEFE0" },
          100: { value: "#FFD4AD" },
          200: { value: "#FFB87A" },
          300: { value: "#FF9B47" },
          400: { value: "#FF7E14" },
          500: { value: "#D36400" },
          600: { value: "#A94F00" },
          700: { value: "#813C00" },
          800: { value: "#5C2A00" },
          900: { value: "#3D1A00" },
          950: { value: "#260F00" },
        },
        yellow: {
          50: { value: "#FFF7DA" },
          100: { value: "#FDEAB0" },
          200: { value: "#FADD85" },
          300: { value: "#F7CF59" },
          400: { value: "#F5C12E" },
          500: { value: "#CFA000" },
          600: { value: "#A28000" },
          700: { value: "#796100" },
          800: { value: "#524200" },
          900: { value: "#382D00" },
          950: { value: "#241C00" },
        },
        teal: {
          50: { value: "#E2F9F8" },
          100: { value: "#A9F1EF" },
          200: { value: "#70E8E5" },
          300: { value: "#37DFDB" },
          400: { value: "#00D5D1" },
          500: { value: "#00B2AE" },
          600: { value: "#008D89" },
          700: { value: "#006865" },
          800: { value: "#004644" },
          900: { value: "#002E2D" },
          950: { value: "#001C1B" },
        },
        cyan: {
          50: { value: "#E4FBFF" },
          100: { value: "#B8F2FE" },
          200: { value: "#8CEAFC" },
          300: { value: "#60E1FB" },
          400: { value: "#34D7FA" },
          500: { value: "#00B7D7" },
          600: { value: "#0092AD" },
          700: { value: "#00718B" },
          800: { value: "#005669" },
          900: { value: "#003B47" },
          950: { value: "#00242C" },
        },
        red: {
          50: { value: "#FFF5F5" },
          100: { value: "#FED7D7" },
          200: { value: "#FEB2B2" },
          300: { value: "#FC8181" },
          400: { value: "#F56565" },
          500: { value: "#E53E3E" },
          600: { value: "#C53030" },
          700: { value: "#9B2C2C" },
          800: { value: "#822727" },
          900: { value: "#63171B" },
          950: { value: "#3A0F12" },
        },
        whiteAlpha: {
          50: { value: "rgba(255, 255, 255, 0.04)" },
          100: { value: "rgba(255, 255, 255, 0.06)" },
          200: { value: "rgba(255, 255, 255, 0.08)" },
          300: { value: "rgba(255, 255, 255, 0.16)" },
          400: { value: "rgba(255, 255, 255, 0.24)" },
          500: { value: "rgba(255, 255, 255, 0.36)" },
          600: { value: "rgba(255, 255, 255, 0.48)" },
          700: { value: "rgba(255, 255, 255, 0.64)" },
          800: { value: "rgba(255, 255, 255, 0.80)" },
          900: { value: "rgba(255, 255, 255, 0.92)" },
          950: { value: "rgba(255, 255, 255, 0.96)" },
        },
        blackAlpha: {
          50: { value: "rgba(0, 0, 0, 0.04)" },
          100: { value: "rgba(0, 0, 0, 0.06)" },
          200: { value: "rgba(0, 0, 0, 0.08)" },
          300: { value: "rgba(0, 0, 0, 0.16)" },
          400: { value: "rgba(0, 0, 0, 0.24)" },
          500: { value: "rgba(0, 0, 0, 0.36)" },
          600: { value: "rgba(0, 0, 0, 0.48)" },
          700: { value: "rgba(0, 0, 0, 0.64)" },
          800: { value: "rgba(0, 0, 0, 0.80)" },
          900: { value: "rgba(0, 0, 0, 0.92)" },
          950: { value: "rgba(0, 0, 0, 0.96)" },
        },
      },
      fonts: {
        heading: {
          value:
            "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
        body: {
          value:
            "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
        mono: {
          value:
            "ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, Consolas, monospace",
        },
      },
      durations: {
        fast: { value: "120ms" },
        base: { value: "180ms" },
        slow: { value: "240ms" },
        slower: { value: "320ms" },
      },
      easings: {
        out: { value: "cubic-bezier(0.16, 1, 0.3, 1)" },
        in: { value: "cubic-bezier(0.7, 0, 0.84, 0)" },
        inOut: { value: "cubic-bezier(0.4, 0, 0.2, 1)" },
        bounce: { value: "cubic-bezier(0.34, 1.56, 0.64, 1)" },
      },
    },
    keyframes: {
      "mail-travel": {
        "0%": { left: "15%", transform: "rotate(0turn)", opacity: "0" },
        "10%": { left: "15%", transform: "rotate(0turn)", opacity: "1" },
        "20%": { left: "15%", transform: "rotate(0turn)", opacity: "1" },
        "80%": { left: "75%", transform: "rotate(1turn)", opacity: "1" },
        "95%": { left: "75%", transform: "rotate(1turn)", opacity: "1" },
        "100%": { left: "75%", transform: "rotate(1turn)", opacity: "0" },
      },
    },
    semanticTokens: {
      colors: {
        bg: {
          DEFAULT: {
            value: { _light: "{colors.white}", _dark: "{colors.black}" },
          },
          supersubtle: {
            value: { _light: "#E6E7EA", _dark: "#1B1C22" },
          },
          subtle: {
            value: { _light: "{colors.gray.50}", _dark: "{colors.gray.950}" },
          },
          muted: {
            value: { _light: "{colors.gray.100}", _dark: "{colors.gray.800}" },
          },
          emphasized: {
            value: { _light: "{colors.gray.200}", _dark: "{colors.gray.700}" },
          },
          inverted: {
            value: { _light: "{colors.black}", _dark: "{colors.white}" },
          },
          panel: {
            value: { _light: "{colors.white}", _dark: "{colors.gray.900}" },
          },
          surface: {
            value: { _light: "{colors.white}", _dark: "{colors.gray.900}" },
          },
          error: {
            value: { _light: "{colors.red.50}", _dark: "{colors.red.950}" },
          },
          warning: {
            value: {
              _light: "{colors.orange.50}",
              _dark: "{colors.orange.950}",
            },
          },
          success: {
            value: { _light: "{colors.green.50}", _dark: "{colors.green.950}" },
          },
          info: {
            value: { _light: "{colors.blue.50}", _dark: "{colors.blue.950}" },
          },
          accent: {
            value: { _light: "{colors.blue.500}", _dark: "{colors.blue.200}" },
          },
        },
        fg: {
          DEFAULT: {
            value: { _light: "{colors.black}", _dark: "{colors.gray.50}" },
          },
          muted: {
            value: { _light: "{colors.gray.600}", _dark: "{colors.gray.400}" },
          },
          subtle: {
            value: { _light: "{colors.gray.400}", _dark: "{colors.gray.500}" },
          },
          inverted: {
            value: { _light: "{colors.gray.50}", _dark: "{colors.black}" },
          },
          error: {
            value: { _light: "{colors.red.500}", _dark: "{colors.red.400}" },
          },
          warning: {
            value: {
              _light: "{colors.orange.600}",
              _dark: "{colors.orange.300}",
            },
          },
          success: {
            value: {
              _light: "{colors.green.600}",
              _dark: "{colors.green.300}",
            },
          },
          info: {
            value: { _light: "{colors.blue.600}", _dark: "{colors.blue.300}" },
          },
          "on-accent": {
            value: { _light: "{colors.white}", _dark: "{colors.gray.900}" },
          },
        },
        border: {
          DEFAULT: {
            value: { _light: "{colors.gray.200}", _dark: "{colors.gray.800}" },
          },
          muted: {
            value: { _light: "{colors.gray.100}", _dark: "{colors.gray.800}" },
          },
          subtle: {
            value: { _light: "{colors.gray.50}", _dark: "{colors.gray.950}" },
          },
          emphasized: {
            value: { _light: "{colors.gray.300}", _dark: "{colors.gray.700}" },
          },
          inverted: {
            value: { _light: "{colors.gray.800}", _dark: "{colors.gray.200}" },
          },
          error: {
            value: { _light: "{colors.red.500}", _dark: "{colors.red.400}" },
          },
          warning: {
            value: {
              _light: "{colors.orange.500}",
              _dark: "{colors.orange.400}",
            },
          },
          success: {
            value: {
              _light: "{colors.green.500}",
              _dark: "{colors.green.400}",
            },
          },
          info: {
            value: { _light: "{colors.blue.500}", _dark: "{colors.blue.400}" },
          },
        },
        blue: {
          solid: {
            value: {
              _light: "{colors.blue.500}",
              _dark: "{colors.blue.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.blue.100}",
              _dark: "{colors.blue.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.blue.700}",
              _dark: "{colors.blue.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.blue.100}",
              _dark: "{colors.blue.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.blue.200}",
              _dark: "{colors.blue.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.blue.300}",
              _dark: "{colors.blue.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.blue.500}",
              _dark: "{colors.blue.200}",
            },
          },
        },
        whiteAlpha: {
          solid: {
            value: {
              _light: "{colors.whiteAlpha.500}",
              _dark: "{colors.whiteAlpha.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.whiteAlpha.100}",
              _dark: "{colors.whiteAlpha.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.whiteAlpha.700}",
              _dark: "{colors.whiteAlpha.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.whiteAlpha.100}",
              _dark: "{colors.whiteAlpha.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.whiteAlpha.200}",
              _dark: "{colors.whiteAlpha.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.whiteAlpha.300}",
              _dark: "{colors.whiteAlpha.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.whiteAlpha.500}",
              _dark: "{colors.whiteAlpha.200}",
            },
          },
        },
        blackAlpha: {
          solid: {
            value: {
              _light: "{colors.blackAlpha.500}",
              _dark: "{colors.blackAlpha.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.blackAlpha.100}",
              _dark: "{colors.blackAlpha.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.blackAlpha.700}",
              _dark: "{colors.blackAlpha.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.blackAlpha.100}",
              _dark: "{colors.blackAlpha.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.blackAlpha.200}",
              _dark: "{colors.blackAlpha.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.blackAlpha.300}",
              _dark: "{colors.blackAlpha.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.blackAlpha.500}",
              _dark: "{colors.blackAlpha.200}",
            },
          },
        },
        gray: {
          solid: {
            value: {
              _light: "{colors.gray.500}",
              _dark: "{colors.gray.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.gray.100}",
              _dark: "{colors.gray.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.gray.700}",
              _dark: "{colors.gray.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.gray.100}",
              _dark: "{colors.gray.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.gray.200}",
              _dark: "{colors.gray.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.gray.300}",
              _dark: "{colors.gray.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.gray.500}",
              _dark: "{colors.gray.200}",
            },
          },
        },
        green: {
          solid: {
            value: {
              _light: "{colors.green.500}",
              _dark: "{colors.green.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.green.100}",
              _dark: "{colors.green.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.green.700}",
              _dark: "{colors.green.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.green.100}",
              _dark: "{colors.green.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.green.200}",
              _dark: "{colors.green.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.green.300}",
              _dark: "{colors.green.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.green.500}",
              _dark: "{colors.green.200}",
            },
          },
        },
        purple: {
          solid: {
            value: {
              _light: "{colors.purple.500}",
              _dark: "{colors.purple.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.purple.100}",
              _dark: "{colors.purple.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.purple.700}",
              _dark: "{colors.purple.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.purple.100}",
              _dark: "{colors.purple.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.purple.200}",
              _dark: "{colors.purple.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.purple.300}",
              _dark: "{colors.purple.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.purple.500}",
              _dark: "{colors.purple.200}",
            },
          },
        },
        pink: {
          solid: {
            value: {
              _light: "{colors.pink.500}",
              _dark: "{colors.pink.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.pink.100}",
              _dark: "{colors.pink.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.pink.700}",
              _dark: "{colors.pink.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.pink.100}",
              _dark: "{colors.pink.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.pink.200}",
              _dark: "{colors.pink.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.pink.300}",
              _dark: "{colors.pink.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.pink.500}",
              _dark: "{colors.pink.200}",
            },
          },
        },
        orange: {
          solid: {
            value: {
              _light: "{colors.orange.500}",
              _dark: "{colors.orange.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.orange.100}",
              _dark: "{colors.orange.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.orange.700}",
              _dark: "{colors.orange.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.orange.100}",
              _dark: "{colors.orange.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.orange.200}",
              _dark: "{colors.orange.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.orange.300}",
              _dark: "{colors.orange.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.orange.500}",
              _dark: "{colors.orange.200}",
            },
          },
        },
        yellow: {
          solid: {
            value: {
              _light: "{colors.yellow.500}",
              _dark: "{colors.yellow.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.yellow.100}",
              _dark: "{colors.yellow.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.yellow.700}",
              _dark: "{colors.yellow.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.yellow.100}",
              _dark: "{colors.yellow.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.yellow.200}",
              _dark: "{colors.yellow.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.yellow.300}",
              _dark: "{colors.yellow.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.yellow.500}",
              _dark: "{colors.yellow.200}",
            },
          },
        },
        teal: {
          solid: {
            value: {
              _light: "{colors.teal.500}",
              _dark: "{colors.teal.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.teal.100}",
              _dark: "{colors.teal.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.teal.700}",
              _dark: "{colors.teal.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.teal.100}",
              _dark: "{colors.teal.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.teal.200}",
              _dark: "{colors.teal.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.teal.300}",
              _dark: "{colors.teal.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.teal.500}",
              _dark: "{colors.teal.200}",
            },
          },
        },
        cyan: {
          solid: {
            value: {
              _light: "{colors.cyan.500}",
              _dark: "{colors.cyan.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.cyan.100}",
              _dark: "{colors.cyan.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.cyan.700}",
              _dark: "{colors.cyan.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.cyan.100}",
              _dark: "{colors.cyan.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.cyan.200}",
              _dark: "{colors.cyan.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.cyan.300}",
              _dark: "{colors.cyan.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.cyan.500}",
              _dark: "{colors.cyan.200}",
            },
          },
        },
        red: {
          solid: {
            value: {
              _light: "{colors.red.500}",
              _dark: "{colors.red.200}",
            },
          },
          contrast: {
            value: {
              _light: "{colors.red.100}",
              _dark: "{colors.red.900}",
            },
          },
          fg: {
            value: {
              _light: "{colors.red.700}",
              _dark: "{colors.red.300}",
            },
          },
          muted: {
            value: {
              _light: "{colors.red.100}",
              _dark: "{colors.red.800}",
            },
          },
          subtle: {
            value: {
              _light: "{colors.red.200}",
              _dark: "{colors.red.700}",
            },
          },
          emphasized: {
            value: {
              _light: "{colors.red.300}",
              _dark: "{colors.red.600}",
            },
          },
          focusRing: {
            value: {
              _light: "{colors.red.500}",
              _dark: "{colors.red.200}",
            },
          },
        },
      },
      // Override Chakra's semantic radius tokens so every recipe that uses
      // them upgrades coherently. l1 powers chips/items/checkmarks/tooltips;
      // l2 powers buttons/inputs/triggers; l3 powers popovers/hovercards/
      // menus/dialogs/drawers/cards/toasts.
      radii: {
        l1: { value: "{radii.md}" },
        l2: { value: "{radii.xl}" },
        l3: { value: "{radii.2xl}" },
      },
    },
    textStyles: {
      display: {
        value: {
          fontSize: "48px",
          lineHeight: "56px",
          letterSpacing: "-0.02em",
          fontWeight: "700",
        },
      },
      "heading.1": {
        value: {
          fontSize: "32px",
          lineHeight: "40px",
          letterSpacing: "-0.015em",
          fontWeight: "700",
        },
      },
      "heading.2": {
        value: {
          fontSize: "24px",
          lineHeight: "32px",
          letterSpacing: "-0.01em",
          fontWeight: "600",
        },
      },
      "heading.3": {
        value: {
          fontSize: "20px",
          lineHeight: "28px",
          letterSpacing: "-0.005em",
          fontWeight: "600",
        },
      },
      "heading.4": {
        value: {
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0",
          fontWeight: "600",
        },
      },
      "body.lg": {
        value: {
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0",
          fontWeight: "400",
        },
      },
      body: {
        value: {
          fontSize: "14px",
          lineHeight: "20px",
          letterSpacing: "0",
          fontWeight: "400",
        },
      },
      "body.sm": {
        value: {
          fontSize: "13px",
          lineHeight: "18px",
          letterSpacing: "0",
          fontWeight: "400",
        },
      },
      label: {
        value: {
          fontSize: "12px",
          lineHeight: "16px",
          letterSpacing: "0.02em",
          fontWeight: "500",
        },
      },
      caption: {
        value: {
          fontSize: "12px",
          lineHeight: "16px",
          letterSpacing: "0",
          fontWeight: "400",
        },
      },
      code: {
        value: {
          fontSize: "13px",
          lineHeight: "20px",
          letterSpacing: "0",
          fontWeight: "400",
          fontFamily: "mono",
        },
      },
    },
    layerStyles: {
      messageRow: {
        value: {
          width: "100%",
          borderRadius: "lg",
          position: "relative",
          px: "10", // Leave room for message tails and avatars.
          outline: "none", // The message bubble carries the focus indicator.
        },
      },
      messageJumpTarget: {
        value: {
          boxShadow: "0 0 0 0 var(--shadow-color)",
          boxShadowColor: "colorPalette.focusRing/35",
          transitionProperty: "box-shadow",
          transitionDuration: "slow",
          transitionTimingFunction: "out",
          _motionReduce: { transition: "none" },
          "[data-message-highlighted=true] &, [data-message-highlighted]:focus-visible &":
            {
              boxShadow: "0 0 0 4px var(--shadow-color)",
            },
        },
      },
      messageImage: {
        value: {
          // The API sends no image dimensions, so the bubble bounds the
          // natural size instead of fixing a crop box.
          display: "block",
          width: "auto",
          height: "auto",
          maxWidth: "250px",
          maxHeight: "320px",
          mx: "auto",
          borderRadius: "lg",
          cursor: "pointer",
          // The photo opens the media viewer from the keyboard too.
          focusVisibleRing: "outside",
        },
      },
      galleryStage: {
        value: {
          // A definite box between the arrows, so the media inside can be
          // bounded by percentages.
          flex: "1",
          minWidth: "0",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
      galleryImage: {
        value: {
          display: "block",
          width: "auto",
          height: "auto",
          maxWidth: "100%",
          maxHeight: "100%",
          borderRadius: "lg",
        },
      },
      fieldFloating: {
        value: {
          position: "relative",
          pb: "4",
        },
      },
      fieldFloatingError: {
        value: {
          position: "absolute",
          fontSize: "2xs",
          bottom: "0",
        },
      },
      fieldsetCompact: {
        value: {
          gap: "1",
        },
      },
      fieldsetInline: {
        value: {
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
        },
      },
      separatorSection: {
        value: {
          marginTop: "4",
          marginBottom: "2",
        },
      },
    },
  },
});

export default FuzeyTheme;
